import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  DisciplinaryListResponse,
  DisciplinaryReport,
  DisciplinaryStats,
  DisciplinaryWrite,
  GeneratePdfResponse,
} from './types'

const ROOT = '/api/disciplinary-reports'

export interface UseReportsOptions {
  status?: string | null
  search?: string
  page?: number
  limit?: number
}

export function useReports(opts: UseReportsOptions = {}) {
  const { status, search, page = 1, limit = 50 } = opts
  return useQuery({
    queryKey: ['disciplinary-reports', 'list', status, search, page, limit],
    queryFn: () => {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (search) params.set('search', search)
      params.set('page', String(page))
      params.set('limit', String(limit))
      return jsonFetch<DisciplinaryListResponse>(`${ROOT}?${params}`)
    },
    staleTime: 15_000,
  })
}

export function useReportStats() {
  return useQuery({
    queryKey: ['disciplinary-reports', 'stats'],
    queryFn: () => jsonFetch<DisciplinaryStats>(`${ROOT}/stats`),
    staleTime: 30_000,
  })
}

export function useReport(id: string | null) {
  return useQuery({
    queryKey: ['disciplinary-reports', 'detail', id],
    queryFn: () => jsonFetch<DisciplinaryReport>(`${ROOT}/${id}`),
    enabled: Boolean(id),
    staleTime: 15_000,
  })
}

export function useCreateReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<DisciplinaryWrite>) =>
      jsonFetch<DisciplinaryReport>(ROOT, { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disciplinary-reports'] })
    },
  })
}

export function useUpdateReport(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      jsonFetch<DisciplinaryReport>(`${ROOT}/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disciplinary-reports'] })
    },
  })
}

export function useGeneratePdf(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      jsonFetch<GeneratePdfResponse>(`${ROOT}/${id}/generate-pdf`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disciplinary-reports', 'detail', id] })
    },
  })
}

export function useDeleteReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ deleted: string }>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disciplinary-reports'] })
    },
  })
}
